// services/dutyTrackingApi.ts
import apiService from './api';
import {
  DutySchedule,
  DutyPeriod,
  CurrentDutyStatus,
  DutyTemplate,
  DutyAnalytics,
  CreateDutyScheduleRequest,
  UpdateDutyScheduleRequest,
  BulkScheduleUploadRequest,
  DutyScheduleListParams,
  CurrentDutyStatusParams,
  DutyAnalyticsParams,
  ScheduleUploadFormData,
  DutyStatusUpdateData,
  ProcessingStatus,
  BulkUploadResponse,
  FileValidationResponse,
  ScheduleConflict,
  DepartmentDutySummary
} from '../types/dutyTracking';

class DutyTrackingApiService {
  private baseUrl = '/duty';

  // Hospital the duty admin page is showing (its on-page hospital selector).
  // Every request carries it as ?hospital=<id>; the backend honours it only
  // for a hospital the admin manages and otherwise answers 403. null = the
  // session (sidebar) hospital, which is also what non-admins always get.
  private hospitalScope: number | null = null;

  setHospitalScope(hospitalId: number | null) {
    this.hospitalScope = hospitalId;
  }

  private withHospital(url: string): string {
    if (!this.hospitalScope) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}hospital=${this.hospitalScope}`;
  }

  // Duty Schedule Management
  async getDutySchedules(params?: DutyScheduleListParams) {
    const queryParams = new URLSearchParams();
    
    if (params?.department) queryParams.append('department', params.department.toString());
    if (params?.specialty) queryParams.append('specialty', params.specialty.toString());
    if (params?.staff_member) queryParams.append('staff_member', params.staff_member.toString());
    if (params?.schedule_month) queryParams.append('schedule_month', params.schedule_month);
    if (params?.processing_status) {
      params.processing_status.forEach(status => queryParams.append('processing_status', status));
    }
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.ordering) queryParams.append('ordering', params.ordering);

    const url = `${this.baseUrl}/schedules/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await apiService.get<{
      count: number;
      next?: string;
      previous?: string;
      results: DutySchedule[];
    }>(this.withHospital(url));
  }

  async getDutySchedule(scheduleId: number) {
    return await apiService.get<DutySchedule>(this.withHospital(`${this.baseUrl}/schedules/${scheduleId}/`));
  }

  async createDutySchedule(data: CreateDutyScheduleRequest) {
    const formData = new FormData();
    
    formData.append('staff_member', data.staff_member.toString());
    formData.append('department', data.department.toString());
    if (data.unit) formData.append('unit', data.unit.toString());
    formData.append('schedule_month', data.schedule_month);
    if (data.file_upload) formData.append('file_upload', data.file_upload);
    if (data.auto_notification_enabled !== undefined) {
      formData.append('auto_notification_enabled', data.auto_notification_enabled.toString());
    }
    if (data.schedule_data) {
      formData.append('schedule_data', JSON.stringify(data.schedule_data));
    }

    return await apiService.post<DutySchedule>(this.withHospital(`${this.baseUrl}/schedules/`), formData);
  }

  async updateDutySchedule(scheduleId: number, data: UpdateDutyScheduleRequest) {
    return await apiService.patch<DutySchedule>(this.withHospital(`${this.baseUrl}/schedules/${scheduleId}/`), data);
  }

  async deleteDutySchedule(scheduleId: number) {
    return await apiService.delete(this.withHospital(`${this.baseUrl}/schedules/${scheduleId}/`));
  }

  async approveDutySchedule(scheduleId: number) {
    return await apiService.post<DutySchedule>(this.withHospital(`${this.baseUrl}/schedules/${scheduleId}/approve/`));
  }

  async rejectDutySchedule(scheduleId: number, reason?: string): Promise<DutySchedule> {
    // The backend has no /duty/schedules/<id>/reject/ route (the call always
    // 404'd). Refuse locally with a clear message instead of a dead request.
    throw new Error('Rejecting a duty schedule is not supported by the server yet.');
  }

  // Bulk Schedule Upload
  async bulkUploadSchedules(data: ScheduleUploadFormData) {
    const formData = new FormData();
    
    formData.append('department', data.department.toString());
    formData.append('schedule_month', data.schedule_month);
    formData.append('auto_notification_enabled', data.auto_notification_enabled.toString());
    
    data.files.forEach((file) => {
      formData.append(`files`, file);
    });

    return await apiService.post<BulkUploadResponse>(this.withHospital(`${this.baseUrl}/schedules/bulk-upload/`), formData);
  }

  // Duty Periods
  async getDutyPeriods(scheduleId?: number, params?: { 
    staff_member?: number;
    department?: number;
    start_date?: string;
    end_date?: string;
    is_current?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    
    if (scheduleId) queryParams.append('schedule', scheduleId.toString());
    if (params?.staff_member) queryParams.append('staff_member', params.staff_member.toString());
    if (params?.department) queryParams.append('department', params.department.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.is_current !== undefined) queryParams.append('is_current', params.is_current.toString());

    const url = `${this.baseUrl}/periods/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await apiService.get<DutyPeriod[]>(this.withHospital(url));
  }

  async getDutyPeriod(periodId: number) {
    return await apiService.get<DutyPeriod>(this.withHospital(`${this.baseUrl}/periods/${periodId}/`));
  }

  async updateDutyPeriod(periodId: number, data: Partial<DutyPeriod>) {
    return await apiService.patch<DutyPeriod>(this.withHospital(`${this.baseUrl}/periods/${periodId}/`), data);
  }

  async startDuty(periodId: number, actualTime?: string) {
    return await apiService.post<DutyPeriod>(this.withHospital(`${this.baseUrl}/periods/${periodId}/start/`), {
      actual_time: actualTime
    });
  }

  async endDuty(periodId: number, actualTime?: string) {
    return await apiService.post<DutyPeriod>(this.withHospital(`${this.baseUrl}/periods/${periodId}/end/`), {
      actual_time: actualTime
    });
  }

  async sendReminder(periodId: number) {
    return await apiService.post<DutyPeriod>(this.withHospital(`${this.baseUrl}/periods/${periodId}/send-reminder/`));
  }

  // Current Duty Status
  async getCurrentDutyStatuses(params?: CurrentDutyStatusParams) {
    const queryParams = new URLSearchParams();
    
    if (params?.department) queryParams.append('current_department', params.department.toString());
    if (params?.unit) queryParams.append('current_unit', params.unit.toString());
    if (params?.duty_status) {
      params.duty_status.forEach(status => queryParams.append('duty_status', status));
    }
    if (params?.availability) {
      params.availability.forEach(avail => queryParams.append('availability', avail));
    }
    if (params?.specialty) queryParams.append('specialty', params.specialty.toString());

    const url = `${this.baseUrl}/status/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiService.get<
      { count: number; results: CurrentDutyStatus[] }
      | CurrentDutyStatus[]
      | { detail?: string }
    >(this.withHospital(url));

    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (typeof response === 'object') {
      const responseObj = response as { count?: number; results?: CurrentDutyStatus[]; detail?: string };

      if (Array.isArray(responseObj.results)) {
        return responseObj.results;
      }

      if (responseObj.detail) {
        throw new Error(responseObj.detail || 'Unable to load current duty statuses');
      }
    }

    return [];
  }

  async getCurrentDutyStatus(staffId: number) {
    return await apiService.get<CurrentDutyStatus>(this.withHospital(`${this.baseUrl}/status/${staffId}/`));
  }

  async updateDutyStatus(staffId: number, data: DutyStatusUpdateData) {
    return await apiService.patch<CurrentDutyStatus>(this.withHospital(`${this.baseUrl}/status/${staffId}/`), data);
  }

  async goOnDuty(staffId: number, dutyPeriodId?: number, departmentId?: number, unitId?: number) {
    return await apiService.post<CurrentDutyStatus>(this.withHospital(`${this.baseUrl}/status/${staffId}/go-on-duty/`), {
      duty_period_id: dutyPeriodId,
      department_id: departmentId,
      unit_id: unitId
    });
  }

  async goOffDuty(staffId: number) {
    return await apiService.post<CurrentDutyStatus>(this.withHospital(`${this.baseUrl}/status/${staffId}/go-off-duty/`));
  }

  async setLocation(staffId: number, departmentId?: number, unitId?: number, context?: string, source?: string) {
    return await apiService.post<CurrentDutyStatus>(this.withHospital(`${this.baseUrl}/status/${staffId}/set-location/`), {
      department_id: departmentId,
      unit_id: unitId,
      location_context: context,
      location_source: source
    });
  }

  // Duty Templates
  async getDutyTemplates(params?: {
    department?: number;
    specialty?: number;
    is_active?: boolean;
    is_default?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    
    if (params?.department) queryParams.append('department', params.department.toString());
    if (params?.specialty) queryParams.append('specialty', params.specialty.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.is_default !== undefined) queryParams.append('is_default', params.is_default.toString());

    const url = `${this.baseUrl}/templates/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await apiService.get<DutyTemplate[]>(this.withHospital(url));
  }

  async getDutyTemplate(templateId: number) {
    return await apiService.get<DutyTemplate>(this.withHospital(`${this.baseUrl}/templates/${templateId}/`));
  }

  async createDutyTemplate(data: Partial<DutyTemplate>) {
    return await apiService.post<DutyTemplate>(this.withHospital(`${this.baseUrl}/templates/`), data);
  }

  async updateDutyTemplate(templateId: number, data: Partial<DutyTemplate>) {
    return await apiService.patch<DutyTemplate>(this.withHospital(`${this.baseUrl}/templates/${templateId}/`), data);
  }

  async deleteDutyTemplate(templateId: number) {
    return await apiService.delete(this.withHospital(`${this.baseUrl}/templates/${templateId}/`));
  }

  async setAsDefaultTemplate(templateId: number) {
    return await apiService.post<DutyTemplate>(this.withHospital(`${this.baseUrl}/templates/${templateId}/set-default/`));
  }

  async incrementTemplateUsage(templateId: number) {
    return await apiService.post<DutyTemplate>(this.withHospital(`${this.baseUrl}/templates/${templateId}/increment-usage/`));
  }

  // Duty Analytics
  async getDutyAnalytics(params: DutyAnalyticsParams) {
    const queryParams = new URLSearchParams();
    
    queryParams.append('department', params.department.toString());
    if (params.specialty) queryParams.append('specialty', params.specialty.toString());
    queryParams.append('period_type', params.period_type);
    queryParams.append('period_start', params.period_start);
    queryParams.append('period_end', params.period_end);

    const url = `${this.baseUrl}/analytics/?${queryParams.toString()}`;
    return await apiService.get<DutyAnalytics>(this.withHospital(url));
  }

  async generateDutyAnalytics(params: DutyAnalyticsParams) {
    return await apiService.post<DutyAnalytics>(this.withHospital(`${this.baseUrl}/analytics/generate/`), params);
  }

  // Utility methods
  async validateScheduleFile(file: File, departmentId: number, scheduleMonth: string): Promise<FileValidationResponse> {
    // The backend has no /duty/validate-file/ route (the call always 404'd).
    // Refuse locally with a clear message instead of a dead request; the
    // client-side checks in DutyTrackingApiService.isValidScheduleFile still apply.
    throw new Error('Server-side schedule file validation is not available yet.');
  }

  async getDepartmentDutySummary(departmentId: number, date?: string) {
    const queryParams = new URLSearchParams();
    if (date) queryParams.append('date', date);

    const url = `${this.baseUrl}/department-summary/${departmentId}/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await apiService.get<DepartmentDutySummary>(this.withHospital(url));
  }

  async getScheduleConflicts(scheduleId: number) {
    return await apiService.get<ScheduleConflict[]>(this.withHospital(`${this.baseUrl}/schedules/${scheduleId}/conflicts/`));
  }

  // File utilities (static methods)
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  static isValidScheduleFile(file: File): { valid: boolean; error?: string } {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const validExtensions = ['.csv', '.xlsx', '.xls'];

    // Check file type
    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidType && !hasValidExtension) {
      return {
        valid: false,
        error: 'Invalid file type. Please upload CSV or Excel files only.'
      };
    }

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size of ${this.formatFileSize(maxSize)}.`
      };
    }

    // Check if file is empty
    if (file.size === 0) {
      return {
        valid: false,
        error: 'File is empty.'
      };
    }

    return { valid: true };
  }

  static getProcessingStatusColor(status: ProcessingStatus): string {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-100',
      processing: 'text-blue-600 bg-blue-100',
      validating: 'text-purple-600 bg-purple-100',
      conflicts: 'text-orange-600 bg-orange-100',
      approved: 'text-green-600 bg-green-100',
      completed: 'text-green-600 bg-green-100',
      failed: 'text-red-600 bg-red-100',
      rejected: 'text-red-600 bg-red-100',
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  }

  static getProcessingStatusIcon(status: ProcessingStatus): string {
    const icons = {
      pending: 'clock',
      processing: 'arrow-path',
      validating: 'magnifying-glass',
      conflicts: 'exclamation-triangle',
      approved: 'check-circle',
      completed: 'check-circle',
      failed: 'x-circle',
      rejected: 'x-circle',
    };
    return icons[status] || 'question-mark-circle';
  }
}

export { DutyTrackingApiService };
export const dutyTrackingApi = new DutyTrackingApiService();
export default dutyTrackingApi;